import { PlugZap, CheckCircle2, AlertTriangle } from "lucide-react";

const fmtPct = (v) => `${(v * 100).toFixed(1)}%`;

function NotConnected({ status }) {
  return (
    <div className="border border-dashed border-slate-300 bg-paper-dark/20 p-6" data-testid="gsc-not-connected">
      <div className="flex items-center gap-2 mono text-[10px] tracking-[0.22em] uppercase text-slate-600">
        <PlugZap className="h-4 w-4 text-orange-survey" /> Google Search Console · not connected
      </div>
      <p className="mt-3 text-sm text-slate-700 max-w-3xl">
        Connect Search Console to see the real Google queries, average positions, clicks and impressions next to each target keyword.
      </p>
      <ol className="mt-4 space-y-1.5 text-sm text-slate-600 list-decimal pl-5 max-w-3xl">
        <li>In Google Cloud, enable the <strong>Search Console API</strong> and create a service account with a JSON key.</li>
        <li>In Search Console → Settings → Users and permissions, add the service-account email as a user on your triaxisxyz.com property.</li>
        <li>Save the JSON key as <code className="mono text-xs bg-white px-1">GSC_SERVICE_ACCOUNT_JSON</code> and the property (e.g. <code className="mono text-xs bg-white px-1">sc-domain:triaxisxyz.com</code>) as <code className="mono text-xs bg-white px-1">GSC_PROPERTY_URL</code>.</li>
      </ol>
      {status?.credentials_present && !status?.connected && (
        <p className="mt-3 text-sm text-rose-700" data-testid="gsc-bad-credentials">Credentials are present but couldn't be read. Check the JSON key and property value.</p>
      )}
    </div>
  );
}

/** Search Console status + top Google queries table. */
export default function GscPanel({ status, report, error, targets }) {
  if (!status) return null;
  if (!status.connected) return <NotConnected status={status} />;
  const isTarget = (q) => targets.has(q.toLowerCase().trim());
  return (
    <div className="border border-slate-200 bg-white" data-testid="gsc-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2 mono text-[10px] tracking-[0.22em] uppercase text-slate-700">
          <CheckCircle2 className="h-4 w-4 text-emerald-700" /> Google Search Console · {status.property}
        </div>
        {report?.start && <span className="mono text-[10px] tracking-[0.18em] uppercase text-slate-400">{report.start} → {report.end}</span>}
      </div>
      {error && (
        <p className="px-4 py-4 text-sm text-rose-700 flex items-center gap-2" data-testid="gsc-error"><AlertTriangle className="h-4 w-4" /> {error}</p>
      )}
      {!error && (
        <div className="overflow-x-auto max-h-[420px]">
          <table className="w-full text-left">
            <thead className="bg-paper-dark/40 sticky top-0">
              <tr>{["Google query", "Clicks", "Impressions", "CTR", "Avg position"].map((c) => <th key={c} className="p-3 mono text-[10px] tracking-[0.18em] uppercase text-slate-500">{c}</th>)}</tr>
            </thead>
            <tbody>
              {(report?.rows || []).slice(0, 100).map((r) => (
                <tr key={r.key} className="border-t border-slate-100" data-testid="gsc-query-row">
                  <td className="p-3 text-sm text-slate-900">
                    {r.key}
                    {isTarget(r.key) && <span className="ml-2 mono text-[9px] tracking-[0.14em] uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5">target</span>}
                  </td>
                  <td className="p-3 mono text-xs">{r.clicks}</td>
                  <td className="p-3 mono text-xs">{r.impressions}</td>
                  <td className="p-3 mono text-xs">{fmtPct(r.ctr)}</td>
                  <td className="p-3 mono text-xs">{r.position.toFixed(1)}</td>
                </tr>
              ))}
              {(report?.rows || []).length === 0 && <tr><td colSpan={5} className="p-4 text-sm text-slate-500">No Search Console data for this range yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
